/*
 * What a widget's period means in dates: the range it covers, the range it is
 * compared with, and the buckets its chart is cut into.
 *
 * Every date here is local — the user's own calendar. The server is told the
 * same zone (`timeZone`) and cuts its buckets on the same days, so a bucket key
 * built here ('2026-10-05') matches the one the aggregate returns.
 */

export const PERIODS = [
    'today', 'yesterday', 'last7Days', 'last30Days', 'thisMonth', 'lastMonth',
    'thisQuarter', 'last3Months', 'last6Months', 'last12Months', 'yearToDate', 'custom',
]

export const GRAINS = ['day', 'week', 'month', 'quarter', 'year']

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds())
const endOfMonth = (d) => endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0))

// The same day n months away, or the last day of that month when it is shorter
const addMonths = (d, n) => {
    const target = new Date(d.getFullYear(), d.getMonth() + n, 1, d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds())
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
    target.setDate(Math.min(d.getDate(), lastDay))
    return target
}

const dayCount = (from, to) => Math.round((startOfDay(to) - startOfDay(from)) / 86400000) + 1

const pad = (n) => String(n).padStart(2, '0')
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const parseDayKey = (key) => {
    const [y, m, d] = key.split('-').map(Number)
    return new Date(y, m - 1, d)
}

/*
 * → { from, to, shift } — `shift` is how far back the previous equivalent
 * period lies. A period that runs "to date" stops today rather than at the end
 * of its month, so it is compared with the same stretch of the period before:
 * the first twelve days of October against the first twelve of September, not
 * against all of it.
 */
export const resolveRange = (period, custom, now = new Date()) => {
    const today = startOfDay(now)
    switch (period) {
        case 'today':        return { from: today, to: endOfDay(today), shift: { days: 1 } }
        case 'yesterday':    return { from: addDays(today, -1), to: endOfDay(addDays(today, -1)), shift: { days: 1 } }
        case 'last7Days':    return { from: addDays(today, -6), to: endOfDay(today), shift: { days: 7 }, step: 'week' }
        case 'last30Days':   return { from: addDays(today, -29), to: endOfDay(today), shift: { days: 30 }, step: 'month' }
        case 'thisMonth':    return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: endOfDay(today), shift: { months: 1 } }
        case 'lastMonth': {
            const from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
            return { from, to: endOfMonth(from), shift: { months: 1 }, wholeMonths: true }
        }
        case 'thisQuarter':  return { from: new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1), to: endOfDay(today), shift: { months: 3 }, step: 'quarter' }
        case 'last3Months':  return { from: new Date(today.getFullYear(), today.getMonth() - 2, 1), to: endOfDay(today), shift: { months: 3 }, step: 'quarter' }
        case 'last6Months':  return { from: new Date(today.getFullYear(), today.getMonth() - 5, 1), to: endOfDay(today), shift: { months: 6 }, step: 'half' }
        case 'last12Months': return { from: new Date(today.getFullYear(), today.getMonth() - 11, 1), to: endOfDay(today), shift: { months: 12 }, step: 'year' }
        case 'yearToDate':   return { from: new Date(today.getFullYear(), 0, 1), to: endOfDay(today), shift: { months: 12 } }
        case 'custom': {
            const from = custom?.from ? startOfDay(parseDayKey(custom.from)) : addDays(today, -29)
            const to = custom?.to ? endOfDay(parseDayKey(custom.to)) : endOfDay(today)
            const [a, b] = from <= to ? [from, to] : [startOfDay(to), endOfDay(from)]
            return { from: a, to: b, shift: { days: dayCount(a, b) } }
        }
        default:             return resolveRange('last30Days', null, now)
    }
}

// A range of whole months moves to whole months — read off its last month, so
// a whole year lands on the 31st of December and not the end of January
const shiftBack = (range, { days, months }) => {
    if (days) return { from: addDays(range.from, -days), to: addDays(range.to, -days) }
    const from = addMonths(range.from, -months)
    return { from, to: range.wholeMonths ? endOfMonth(addMonths(range.to, -months)) : addMonths(range.to, -months) }
}

/*
 * The period `offset` steps away from the one resolved — −1 is the one before,
 * +1 the one after, offered while it has begun (usePeriod). A step is the
 * period's own length. One counted in days (today, a custom range) moves by
 * that many days. One counted in months becomes whole calendar months once it
 * leaves the present: "this month" a step back is all of September, not
 * September up to today's date, and "year to date" is the whole of last year.
 *
 * A rolling window with a `step` steps through calendar periods instead of
 * its own length, which would land on dates nobody reports by: "last 7 days"
 * goes back a Monday-to-Sunday week at a time, "last 30 days" a month, "last
 * 3 months" a calendar quarter, "last 6 months" a half-year, "last 12
 * months" a calendar year.
 */
const STEP_MONTHS = { month: 1, quarter: 3, half: 6, year: 12 }

export const offsetRange = (range, offset) => {
    if (!offset) return range
    if (range.step === 'week') {
        const from = addDays(truncate(range.to, 'week'), offset * 7)
        return { from, to: endOfDay(addDays(from, 6)), shift: { days: 7 }, wholeWeek: true }
    }
    const size = STEP_MONTHS[range.step]
    if (size) {
        // The calendar period today is in, then `offset` of them away
        const end = range.to
        const from = new Date(end.getFullYear(), Math.floor(end.getMonth() / size) * size + offset * size, 1)
        const to = endOfMonth(new Date(from.getFullYear(), from.getMonth() + size - 1, 1))
        return { from, to, shift: { months: size }, wholeMonths: true, calendar: range.step }
    }
    const { days, months } = range.shift
    if (days) return { from: addDays(range.from, offset * days), to: addDays(range.to, offset * days), shift: range.shift }
    const from = new Date(range.from.getFullYear(), range.from.getMonth() + offset * months, 1)
    const to = endOfMonth(new Date(from.getFullYear(), from.getMonth() + months - 1, 1))
    return { from, to, shift: range.shift, wholeMonths: true }
}

// The ISO week of a Monday, and the year that week belongs to — the year of
// its Thursday, so 29 Dec 2025 is week 1 of 2026
export const isoWeek = (monday) => {
    const thursday = addDays(startOfDay(monday), 3)
    const year = thursday.getFullYear()
    return { week: Math.floor((dayCount(new Date(year, 0, 1), thursday) - 1) / 7) + 1, year }
}

/*
 * A stepped period written out, as briefly as its dates allow:
 * "Week 43, 2026", "Q3 2026", "H1 2026", "September 2026", "2025", "May – Jul 2026",
 * "2 – 31 Aug 2026", "29 Oct 2026". formatRange drops what the two ends share,
 * in the order the locale writes it. `weekText(week, year)` words a whole week;
 * without it the week is written as its dates.
 */
export const periodLabel = ({ from, to, wholeMonths, wholeWeek, calendar }, locale, weekText) => {
    if (wholeWeek && weekText) {
        const { week, year } = isoWeek(from)
        return weekText(week, year)
    }
    // Written as the chart's quarter buckets are (bucketLabel), with the full year
    if (calendar === 'quarter') return `Q${Math.floor(from.getMonth() / 3) + 1} ${from.getFullYear()}`
    if (calendar === 'half') return `H${from.getMonth() < 6 ? 1 : 2} ${from.getFullYear()}`
    const sameMonth = from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()
    if (wholeMonths && sameMonth) return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(from)
    if (wholeMonths && from.getMonth() === 0 && to.getMonth() === 11 && from.getFullYear() === to.getFullYear()) return String(from.getFullYear())
    const opts = wholeMonths ? { month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short', year: 'numeric' }
    const fmt = new Intl.DateTimeFormat(locale, opts)
    return dayKey(from) === dayKey(to) ? fmt.format(from) : fmt.formatRange(from, to)
}

// 'previous' — the period just before; 'lastYear' — the same dates a year back
export const compareRange = (range, mode) => {
    if (mode === 'none' || !mode) return null
    if (mode === 'lastYear') return shiftBack(range, { months: 12 })
    return shiftBack(range, range.shift)
}

// The grain that keeps a chart between roughly seven and thirty-one bars
export const autoGrain = ({ from, to }) => {
    const days = dayCount(from, to)
    if (days <= 35) return 'day'
    if (days <= 120) return 'week'
    if (days <= 800) return 'month'
    return 'quarter'
}

export const truncate = (d, grain) => {
    switch (grain) {
        case 'week': {
            const day = (d.getDay() + 6) % 7 // Monday is 0, as date_trunc has it
            return addDays(startOfDay(d), -day)
        }
        case 'month':   return new Date(d.getFullYear(), d.getMonth(), 1)
        case 'quarter': return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)
        case 'year':    return new Date(d.getFullYear(), 0, 1)
        default:        return startOfDay(d)
    }
}

const step = (d, grain) => {
    switch (grain) {
        case 'week':    return addDays(d, 7)
        case 'month':   return new Date(d.getFullYear(), d.getMonth() + 1, 1)
        case 'quarter': return new Date(d.getFullYear(), d.getMonth() + 3, 1)
        case 'year':    return new Date(d.getFullYear() + 1, 0, 1)
        default:        return addDays(d, 1)
    }
}

// Every bucket the range touches, in order — the empty ones too, so a day
// with no sales is a zero on the axis rather than a gap closed over
export const bucketKeys = ({ from, to }, grain) => {
    const keys = []
    for (let d = truncate(from, grain); d <= to && keys.length < 1000; d = step(d, grain)) keys.push(dayKey(d))
    return keys
}

// The parameters every aggregate read takes; the route passes on only those
// the aggregate declares
export const rangeParams = ({ from, to }, grain) => ({
    fromDate: from.toISOString(),
    toDate: to.toISOString(),
    grain,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
})

// The same range as a list filter — the list reads `from`/`to` on `dateField`
export const rangeFilter = ({ from, to }, dateField = 'date') =>
    `from:${from.toISOString()};to:${to.toISOString()};dateField:${dateField}`

/*
 * A bucket's name on the axis (`short`) and in the tooltip (`long`). A month
 * carries its year only when the range spans more than one.
 */
export const bucketLabel = (key, grain, locale, { long = false, spansYears = false } = {}) => {
    const d = parseDayKey(key)
    const fmt = (opts) => new Intl.DateTimeFormat(locale, opts).format(d)
    switch (grain) {
        case 'week':    return long ? fmt({ day: 'numeric', month: 'short', year: 'numeric' }) : fmt({ day: 'numeric', month: 'short' })
        case 'month':   return long || spansYears ? fmt({ month: 'short', year: '2-digit' }) : fmt({ month: 'short' })
        case 'quarter': return `Q${Math.floor(d.getMonth() / 3) + 1} ${String(d.getFullYear()).slice(2)}`
        case 'year':    return String(d.getFullYear())
        default:        return long ? fmt({ weekday: 'short', day: 'numeric', month: 'short' }) : fmt({ day: 'numeric', month: 'short' })
    }
}

export const spansYears = ({ from, to }) => from.getFullYear() !== to.getFullYear()

// The range written out, for the header of a custom period
export const rangeLabel = ({ from, to }, locale) => {
    const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' })
    return `${fmt.format(from)} – ${fmt.format(to)}`
}
